import { FormikValues } from "formik/dist/types";
import { isPlainObject } from "lodash";
import { datadogLogs } from "@datadog/browser-logs";
import { reach, Schema } from "yup";
import { xmlStringDiffer } from "./xmlStringDiffer";

type Reacher = <T>(path: string) => Schema<T>;
type CustomHandlers = Record<string, DiffMethod | null>;
/**
 * A diff method  will take the initial and current paramseters are return and array of name
 * differences.
 * @initial - The initial value
 * @current - The current value
 * @reach - A method to react into a schema for a given path and return the schema of that field
 * @depth - How deep into the tree are we.
 * @nameTraverse - The name to the current set of fields, eg base.field
 */
export type DiffMethod = (
  initial: unknown,
  cur: unknown,
  reach?: Reacher,
  depth?: number,
  nameTraverse?: string
) => Array<string>;
function isRecord(test: unknown): test is Record<string, unknown> {
  return isPlainObject(test);
}
const diffArray: DiffMethod = () => [];
export const CustomFieldHandlers: CustomHandlers = {
  xmlString: xmlStringDiffer,
  array: diffArray
};
export const diffObject: DiffMethod = (
  initial: unknown,
  cur: unknown,
  reach,
  depth: number = 0,
  nameTraverse: string = ""
): Array<string> => {
  if (depth >= 500) {
    //I really hope we are not super nesting form values.
    datadogLogs.logger.warn("Exceeding depth for form fields");
    return [];
  }
  if (!isRecord(cur) || !isRecord(initial)) {
    return [];
  }
  return Object.keys(cur).reduce((diffs, key) => {
    const i = initial[key];
    const v = cur[key];
    if (v === i) {
      return diffs;
    }
    const diffKey = nameTraverse ? `${nameTraverse}.${key}` : key;
    let diffMethod: DiffMethod | null = () => [diffKey];
    const eitherIsArray = Array.isArray(i) || Array.isArray(v);
    if (eitherIsArray) {
      //No deep checking Arrays
      diffMethod = CustomFieldHandlers["array"];
    }
    const eitherIsObject = isPlainObject(i) || isPlainObject(v);
    if (eitherIsObject) {
      diffMethod = CustomFieldHandlers["object"];
    }
    const getKey = () => {
      try {
        return reach ? reach(diffKey).describe().type : null;
      } catch (e) {
        console.warn("Error when attempting to describe key", {
          diffKey
        });
      }
      return null;
    };
    const customKey = getKey();
    if (customKey) {
      const customMethod = CustomFieldHandlers[customKey];
      if (customMethod) {
        diffMethod = customMethod;
      }
    }
    if (diffMethod) {
      return diffs.concat(diffMethod(i, v, reach, depth + 1, diffKey));
    }
    return diffs;
  }, [] as Array<string>);
};
CustomFieldHandlers["object"] = diffObject;

export type FieldDiffer = <Values = any>(
  initialValues: Values,
  current: Values,
  schema?: Schema<any>
) => string[];
export let diffFields: FieldDiffer = <
  Values extends FormikValues = FormikValues
>(
  initialValues: Values,
  current: Values,
  schema?: Schema<any>
) => {
  const reacher = schema ? (path: string) => reach(schema, path) : undefined;
  return diffObject(initialValues, current, reacher);
};
